import { ChangeEvent, FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ModalProps,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { type UpdateApplicationDTO } from '../../../../api';

type EditApplicationModalProps = {
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
  fields: UpdateApplicationDTO['body'] | null;
  fieldsHandler: (
    e: SelectChangeEvent | ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  isLoading: boolean;
};

export const EditApplicationModal: FC<EditApplicationModalProps> = (props) => {
  const { open, onClose, onEdit, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // validation for required fields
  const requiredFields = ['name'] as (keyof UpdateApplicationDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }

    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    onClose();
  };
  const closeHandler = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>{t('abf-pay-apps.modals.edit.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="name"
            value={fields?.name ?? ''}
            onChange={(e) => fieldsHandler(e)}
            label={t('abf-pay-apps.modals.edit.fields.name')}
            fullWidth
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={() => onEdit()}
          disabled={isLoading || checkSubmitDisabling()}
        >
          {t('abf-pay-apps.modals.edit.save-btn')}
        </Button>
        <Button variant="outlined" onClick={() => onClose()}>
          {t('abf-pay-apps.modals.cancel-btn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
